import { useMemo, useState } from 'react';
import { FormField, FormSelect } from '@practium/ui';
import { COFFEE_LABELS, COFFEE_OPTIONS, LIMING_CODES, PARAM_LABELS, PERCENT_CODES } from '../../constants/parameters.constants';
import { useTableList } from '../../hooks/useTableList';
import type { CoffeeType, ReferenceSectionProps, StateReferenceLiming } from '../../types/parameters.types';
import { formatNumber } from '../../utils/format';
import { ValuesFormModal } from '../Forms/ValuesFormModal';
import { type ReferenceColumn } from '../Tables/ReferenceTable';
import { SectionList } from '../Tables/SectionList';
import tableStyles from '../Tables/ParamsTable.module.css';

type ModalState = { type: 'closed' } | { type: 'open'; item: StateReferenceLiming | null };

const paramLabel = (code: string) => `${PARAM_LABELS[code] ?? code}${PERCENT_CODES.includes(code) ? ' (%)' : ''}`;

const COLUMNS: ReferenceColumn<StateReferenceLiming>[] = [
	{
		key: 'coffee',
		header: 'Tipo de café',
		render: (item) => <span className={tableStyles.cellPrimary}>{COFFEE_LABELS[item.coffeeType] ?? item.coffeeType}</span>,
	},
	...LIMING_CODES.map((code) => ({
		key: code,
		header: paramLabel(code),
		render: (item: StateReferenceLiming) => (
			<span className={tableStyles.cellValue}>
				{formatNumber(item.parameters.find((parameter) => parameter.code === code)?.value)}
			</span>
		),
	})),
];

const FIELDS = LIMING_CODES.map((code) => ({ key: code, label: paramLabel(code) }));

export const LimingSection = ({ reference, isSaving, onSave, onConfirmDelete }: ReferenceSectionProps) => {
	const [modal, setModal] = useState<ModalState>({ type: 'closed' });
	const [coffeeType, setCoffeeType] = useState<CoffeeType>('cafeArabica');
	const list = useTableList({ items: reference.liming });

	const availableOptions = useMemo(
		() => COFFEE_OPTIONS.filter((option) => !reference.liming.some((item) => item.coffeeType === option.value)),
		[reference.liming]
	);

	const openNew = () => {
		setCoffeeType((availableOptions[0]?.value as CoffeeType) ?? 'cafeArabica');
		setModal({ type: 'open', item: null });
	};

	const handleSubmit = (editing: StateReferenceLiming | null, values: Record<string, number>) => {
		const others = editing?.parameters.filter((item) => !LIMING_CODES.includes(item.code)) ?? [];
		const parameters = [...LIMING_CODES.map((code) => ({ code, value: values[code] })), ...others];
		const liming = editing
			? reference.liming.map((item) => (item.coffeeType === editing.coffeeType ? { ...item, parameters } : item))
			: [...reference.liming, { coffeeType, parameters }];
		return onSave({ liming }, 'Calagem salva com sucesso!');
	};

	const handleDelete = async (target: StateReferenceLiming) => {
		if (await onConfirmDelete(`a calagem de ${COFFEE_LABELS[target.coffeeType]}`)) {
			await onSave(
				{ liming: reference.liming.filter((item) => item.coffeeType !== target.coffeeType) },
				'Calagem excluída com sucesso!'
			);
		}
	};

	return (
		<>
			<SectionList
				list={list}
				columns={COLUMNS}
				getRowId={(item) => item.coffeeType}
				unit={['tipo de café', 'tipos de café']}
				actionLabel={availableOptions.length > 0 ? 'Nova calagem' : undefined}
				onAction={openNew}
				onEdit={(item) => setModal({ type: 'open', item })}
				onDelete={handleDelete}
			/>
			{modal.type === 'open' && (
				<ValuesFormModal
					title={modal.item ? `Editar calagem: ${COFFEE_LABELS[modal.item.coffeeType]}` : 'Nova calagem'}
					sectionTitle="Parâmetros"
					fields={FIELDS}
					initialValues={Object.fromEntries(
						LIMING_CODES.map((code) => [code, modal.item?.parameters.find((item) => item.code === code)?.value])
					)}
					isSaving={isSaving}
					onSubmit={(values) => handleSubmit(modal.item, values)}
					onClose={() => setModal({ type: 'closed' })}
				>
					{!modal.item && (
						<FormField label="Tipo de café" required>
							<FormSelect
								value={coffeeType}
								options={availableOptions}
								onChange={(value) => setCoffeeType(value as CoffeeType)}
							/>
						</FormField>
					)}
				</ValuesFormModal>
			)}
		</>
	);
};
